if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        // The "thumbnail" desktop layout hides every non-active slide via
        // display:none, so only one slide ever has a non-zero width. Dawn's
        // native slider arrows navigate by scroll position, which never
        // changes when there's nothing else to scroll to -- so the main
        // viewer's own prev/next buttons need their own handler that steps
        // through slides by index instead.
        if (this.dataset.desktopLayout.includes('thumbnail')) {
          this.initViewerArrows();
        }

        this.initMobileArrowLooping();

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // Below 750px the gallery is a real scroll-snap slider and Dawn stops it
      // dead at the last image: update() disables the next button once the last
      // slide is visible, so a disabled button emits no click and there is
      // nothing left to hook. Desktop already wraps around (see initViewerArrows),
      // so mobile stopping is an inconsistency rather than a decision.
      //
      // enableSliderLooping is Dawn's own flag and is the right lever: update()
      // sets the counter and fires slideChanged BEFORE checking it, so the only
      // thing it suppresses is the disabling. The wrap itself is handled here in
      // the capture phase, which runs before the button's own bubble listener --
      // so stopPropagation keeps Dawn's onButtonClick from also scrolling, and
      // every other click falls straight through to it untouched.
      initMobileArrowLooping() {
        const viewer = this.elements.viewer;
        if (!viewer || !viewer.slider) return;

        viewer.enableSliderLooping = true;
        // Dawn renders the previous button disabled for the first slide and
        // relies on update() to clear it -- but update() now returns before
        // reaching that, so it would stay disabled for good. Clear it once.
        viewer
          .querySelectorAll('button[name="previous"], button[name="next"]')
          .forEach((button) => button.removeAttribute('disabled'));

        viewer.addEventListener(
          'click',
          (event) => {
            if (this.mql.matches) return;

            const button = event.target.closest('button[name="next"], button[name="previous"]');
            if (!button || !viewer.contains(button)) return;

            const slider = viewer.slider;
            const maxScroll = slider.scrollWidth - slider.clientWidth;
            // A couple of pixels of slack: scrollLeft is fractional under a
            // scroll-snap container and rarely lands exactly on the boundary.
            const atEnd = slider.scrollLeft >= maxScroll - 2;
            const atStart = slider.scrollLeft <= 2;

            if (button.name === 'next' && atEnd) {
              event.preventDefault();
              event.stopPropagation();
              slider.scrollTo({ left: 0 });
            } else if (button.name === 'previous' && atStart) {
              event.preventDefault();
              event.stopPropagation();
              slider.scrollTo({ left: maxScroll });
            }
          },
          true
        );
      }

      initViewerArrows() {
        const prevButton = this.elements.viewer.querySelector('.slider-button--prev');
        const nextButton = this.elements.viewer.querySelector('.slider-button--next');
        if (!prevButton || !nextButton) return;

        const step = (direction) => (event) => {
          // Desktop only. Below 750px every slide is visible and Dawn's own
          // scroll-based arrows page through them correctly -- and this handler
          // actively breaks them, because setActiveMedia(prepend) moves the
          // target slide to the front of the list and scrolls to its new
          // offsetLeft of 0. The result is that "next" always lands on the first
          // position, so the gallery ping-pongs 1 -> 2 -> 1 instead of cycling.
          // Checked per click rather than at bind time so a resize across the
          // breakpoint needs no rebinding.
          if (!this.mql.matches) return;

          event.preventDefault();
          // Slides only. [data-media-id] also matches the .product__media-toggle
          // button inside each slide, so this list came back twice as long as the
          // gallery -- stepping by index then landed on the *same* media's button
          // as often as on the next slide, which is why the arrows shuffled
          // between two images instead of walking the gallery. [id^="Slide-"] is
          // what Dawn's own SliderComponent treats as the slide set.
          const items = Array.from(this.elements.viewer.querySelectorAll('[id^="Slide-"]'));
          if (!items.length) return;
          const activeIndex = items.findIndex((item) => item.classList.contains('is-active'));
          if (activeIndex === -1) return;
          const nextIndex = (activeIndex + direction + items.length) % items.length;
          // prepend: false. Prepending moves the chosen slide to the front of the
          // list AND reorders the thumbnail strip under it, so the running order
          // changed on every click and the next index no longer meant what it did
          // a moment ago. Stepping needs a stable list; it is only the variant
          // change that wants the featured image pulled to the front.
          this.setActiveMedia(items[nextIndex].dataset.mediaId, false);
        };

        prevButton.addEventListener('click', step(-1));
        nextButton.addEventListener('click', step(1));
      }

      onSlideChanged(event) {
        const activeElement = event.detail.currentElement;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${activeElement.dataset.mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
        this.syncVariantToSlide(activeElement);
      }

      // Reverse of the variant->image link: when the shopper navigates the
      // slider (arrows, swipe, or thumbnail click) to an image that's the
      // assigned Variant Image for some Design, select that Design so the
      // active pill and price/availability follow the currently shown image.
      // data-design-value is set server-side (product-media-gallery.liquid)
      // from whichever variant has this media as its featured_media.
      //
      // Match only against the Design radios. Every option's radios live in the
      // same variant-selects element, so matching on value alone let a slide
      // labelled with, say, "80cm x 150cm" reach into the Size group -- and
      // because setActiveMedia runs as part of Dawn's own variant update, that
      // fed straight back into another variant change and undid the shopper's
      // choice. data-option-name comes from product-variant-options.liquid.
      syncVariantToSlide(activeElement) {
        const designValue = (activeElement.dataset.designValue || '').trim();
        if (!designValue) return;

        const variantSelects = this.closest('product-info')?.querySelector('variant-selects');
        if (!variantSelects) return;

        const matchingInput = Array.from(
          variantSelects.querySelectorAll('input[type="radio"][data-option-name="Design"]')
        ).find((input) => input.value.trim().toLowerCase() === designValue.toLowerCase());
        if (!matchingInput || matchingInput.checked) return;

        matchingInput.checked = true;
        matchingInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.syncVariantToSlide(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
